import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { bgSlate900 } from '@osmosis/tailwind-ui/styles';
import { IconButton } from '@osmosis/tailwind-ui/components/IconButton';
import { IconButtonProps } from '@osmosis/tailwind-ui/components/IconButton';
import { Tooltip } from '@osmosis/tailwind-ui/components/tooltip/Tooltip';
import { P } from '@osmosis/tailwind-ui/components/Text';
import { faClock } from '@fortawesome/pro-solid-svg-icons/faClock';
import { faClock as faClockRegular } from '@fortawesome/pro-regular-svg-icons/faClock';
import { Icon } from '@osmosis/tailwind-ui/components/Icon';

import useModal from '../../../hooks/useModal';
import { PLAYLIST_TRACK_EVENT } from '../../../context/PlaylistProvider';
import { useFetch } from '../../../context/FetchProvider';
import { usePlaylistContext } from '../../../context/PlaylistProvider';
import { useSnackbarContext } from '../../../context/SnackbarProvider';
import { useUserContext } from '../../../context/UserContext';

import ExpiredUserLockoutModal from '../../../components/learn/ExpiredUserModal';
import { ExpiredUserModalType } from '../../../constants/ExpiredUserModalConfig';

import {
  addToPlaylist,
  removeFromPlaylist,
} from '../../../lib/frontend/playlist';
import { track } from '../../../lib/util/tracking';

type Props = {
  video: { learnNodeID: number; name: string };
  iconButtonProps?: IconButtonProps;
  ['data-testid']: string;
};

/** Button to add an item to the watch later playlist. */
const AddToWatchLaterButton = (props: Props) => {
  const { video, iconButtonProps = {} } = props;
  const { hasPrime } = useUserContext();

  const { onAddSnackBar } = useSnackbarContext();
  const { savedPlaylist: watchLaterPlaylist, updatePlaylists } =
    usePlaylistContext();
  const { post } = useFetch();
  const intl = useIntl();
  const lockoutModal = useModal();

  const isAdded = !!watchLaterPlaylist?.list?.find(
    (i) => i.data.nodeID === video.learnNodeID,
  );

  // FIXME: any
  const handleClick = async (e: any) => {
    e.stopPropagation();

    if (!hasPrime) {
      lockoutModal.show();

      return;
    }

    const trackData = {
      action: isAdded ? 'remove-video' : 'add-video',
      playlistID: watchLaterPlaylist?.url,
      is_owner: true,
      is_follower: false,
      source: 'web-watch-later-btn',
      content_type: 'video',
      nodeIDs: [video.learnNodeID],
    };

    if (isAdded) {
      await removeFromPlaylist(post, {
        special: 1,
        nodeIDs: [video.learnNodeID],
      } as any); // FIXME: any

      onAddSnackBar({
        message: intl.formatMessage(
          {
            defaultMessage: '{name} removed from the playlist "Watch later"',
            id: 'watch_later_button.snackbar.removed-text',
          },
          {
            name: video.name,
          },
        ),
        variant: 'bg-gray-800',
      });
    } else {
      await addToPlaylist(post, { special: 1, nodeIDs: [video.learnNodeID] });
      onAddSnackBar({
        message: intl.formatMessage(
          {
            defaultMessage: '{name} added to the playlist "Watch later"',
            id: 'watch_later_button.snackbar.added-text',
          },
          {
            name: video.name,
          },
        ),
        variant: 'bg-gray-800',
      });
    }

    updatePlaylists();
    track(PLAYLIST_TRACK_EVENT, trackData, ['keen', 'pendo']);
  };

  const ariaLabel = intl.formatMessage({
    id: 'common.watchLater',
    defaultMessage: 'Add to watch later',
  });

  return (
    <>
      {!hasPrime && (
        <ExpiredUserLockoutModal
          {...lockoutModal}
          modalType={ExpiredUserModalType.PLAYLIST}
        />
      )}

      <Tooltip id="add-to-watch-later">
        <P className="!text-sm text-inherit">
          {isAdded ? (
            <FormattedMessage
              defaultMessage="Added to watch later"
              id="watch_later.tooltip.added.text"
            />
          ) : (
            <FormattedMessage
              defaultMessage="Add to watch later"
              id="watch_later.tooltip.not_added.text"
            />
          )}
        </P>
      </Tooltip>

      <IconButton
        data-tip
        data-tooltip-id="add-to-watch-later"
        data-testid={props['data-testid']}
        aria-label={ariaLabel}
        className={`${bgSlate900} text-white text-[22px]`}
        icon={
          isAdded ? <Icon icon={faClock} /> : <Icon icon={faClockRegular} />
        }
        onClick={handleClick}
        {...iconButtonProps}
      />
    </>
  );
};

export default AddToWatchLaterButton;
